/**
 * Before sending page context to Claude, we stamp every visible,
 * interactive element with a short `data-koko-ref="xN"` attribute. That
 * ref is the anchor Claude uses when returning `[POINT:xN:label]` tags,
 * and lets us locate the exact element afterwards via `querySelector`.
 *
 * We write into the host page's DOM — which means we own the attribute
 * name and must clean it up after every turn so we don't leak state.
 */

export interface TaggedElement {
  /** The short base36 ref assigned for this turn. */
  ref: string;
  element: HTMLElement;
  /** Best-effort human-readable label extracted from the element. */
  label: string;
  /** Element role — tag name or an explicit aria role. */
  role: string;
  /** href (links) / action (forms) / placeholder (inputs) — whichever applies. */
  detail: string;
}

const INTERACTIVE_SELECTOR = [
  "a[href]",
  "button",
  "input:not([type=hidden])",
  "select",
  "textarea",
  "[role=button]",
  "[role=link]",
  "[role=tab]",
  "[role=menuitem]",
  "[role=checkbox]",
  "[contenteditable=true]",
  "[tabindex]:not([tabindex='-1'])",
  "summary",
].join(",");

const REF_ATTRIBUTE = "data-koko-ref";

/** Tags every visible interactive element in the viewport. Returns the mapping. */
export function tagInteractiveElements(): TaggedElement[] {
  // Defensive cleanup: strip any leftover refs from a prior turn.
  clearInteractiveElementTags();

  const candidates = document.querySelectorAll<HTMLElement>(INTERACTIVE_SELECTOR);
  const tagged: TaggedElement[] = [];
  let counter = 0;

  candidates.forEach((element) => {
    if (!isElementVisibleInViewport(element)) return;
    const ref = toBase36Ref(counter);
    element.setAttribute(REF_ATTRIBUTE, ref);
    tagged.push({
      ref,
      element,
      label: extractLabel(element),
      role: extractRole(element),
      detail: extractDetail(element),
    });
    counter += 1;
  });

  return tagged;
}

/** Removes every `data-koko-ref` from the host DOM. Idempotent. */
export function clearInteractiveElementTags(): void {
  const tagged = document.querySelectorAll<HTMLElement>(`[${REF_ATTRIBUTE}]`);
  tagged.forEach((element) => element.removeAttribute(REF_ATTRIBUTE));
}

function toBase36Ref(counter: number): string {
  return `x${counter.toString(36)}`;
}

function isElementVisibleInViewport(element: HTMLElement): boolean {
  // Gate on CSS visibility only, not viewport position. Elements below
  // the fold are still legitimate pointing targets — `ElementLocator`
  // smooth-scrolls them into view before the bird flies. The DOM
  // summary already includes an `[above-viewport]` / `[below-viewport]`
  // hint so Claude can pick targets with full awareness.
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
    return false;
  }
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  return true;
}

function extractLabel(element: HTMLElement): string {
  const ariaLabel = element.getAttribute("aria-label");
  if (ariaLabel) return truncateWhitespace(ariaLabel);

  const ariaLabelledBy = element.getAttribute("aria-labelledby");
  if (ariaLabelledBy) {
    const labelledNode = document.getElementById(ariaLabelledBy);
    if (labelledNode?.textContent) return truncateWhitespace(labelledNode.textContent);
  }

  if (element instanceof HTMLInputElement) {
    if (element.labels && element.labels.length > 0) {
      return truncateWhitespace(element.labels[0].textContent ?? "");
    }
    if (element.placeholder) return truncateWhitespace(element.placeholder);
    if (element.name) return `input[name=${element.name}]`;
  }

  if (element instanceof HTMLImageElement && element.alt) {
    return truncateWhitespace(element.alt);
  }

  const innerText = element.innerText ?? element.textContent ?? "";
  return truncateWhitespace(innerText);
}

function extractRole(element: HTMLElement): string {
  const explicitRole = element.getAttribute("role");
  if (explicitRole) return explicitRole;
  return element.tagName.toLowerCase();
}

function extractDetail(element: HTMLElement): string {
  if (element instanceof HTMLAnchorElement && element.href) {
    // Show path only to keep the prompt compact.
    try {
      const url = new URL(element.href);
      return `href=${url.pathname || "/"}`;
    } catch {
      return `href=${element.href}`;
    }
  }
  if (element instanceof HTMLInputElement) {
    return `type=${element.type || "text"}`;
  }
  if (element instanceof HTMLButtonElement && element.type === "submit") {
    return "type=submit";
  }
  return "";
}

function truncateWhitespace(text: string, maxLength = 80): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length <= maxLength) return collapsed;
  return collapsed.slice(0, maxLength - 1) + "…";
}
